import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { brokerApi } from "@/hooks/useBrokerApi";

const GroupMembers = ({ group, network, onToggleMember }) => (
  <div className="mt-4 pt-4 border-t border-rr-line flex flex-wrap gap-2">
    {network.length === 0 && <p className="text-xs text-rr-muted">No connected drivers yet.</p>}
    {network.map((c) => (
      <button
        key={c.id}
        onClick={() => onToggleMember(group, c.driver_id)}
        data-testid={`group-member-chip-${group.id}-${c.driver_id}`}
        className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
          group.driver_ids.includes(c.driver_id) ? "bg-rr-navy text-white border-rr-navy" : "border-rr-line text-rr-muted"
        }`}
      >
        {c.invited_name}
      </button>
    ))}
  </div>
);

const GroupRow = ({ group: g, network, expanded, onToggleExpand, onRemove, onToggleMember }) => (
  <div data-testid={`broker-group-row-${g.id}`} className="bg-white border border-rr-line rounded-xl p-5">
    <div className="flex items-center justify-between gap-3">
      <p className="font-heading font-bold text-rr-navy">
        {g.name} <span className="text-xs text-rr-muted font-normal">({g.driver_ids.length} drivers)</span>
      </p>
      <div className="flex items-center gap-3">
        <button onClick={() => onToggleExpand(g.id)} data-testid={`broker-group-manage-toggle-${g.id}`} className="text-xs font-bold text-rr-orange hover:underline">
          Manage Members
        </button>
        <button onClick={() => onRemove(g.id)} data-testid={`broker-group-delete-button-${g.id}`} className="text-rr-red hover:opacity-70">
          <Trash2 size={15} />
        </button>
      </div>
    </div>
    {expanded && <GroupMembers group={g} network={network} onToggleMember={onToggleMember} />}
  </div>
);

export const BrokerGroupsTab = () => {
  const [groups, setGroups] = useState(null);
  const [network, setNetwork] = useState([]);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = () => brokerApi.get("/groups").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
  useEffect(() => {
    load();
    brokerApi.get("/network").then(({ data }) => setNetwork(data.filter((c) => c.driver_id))).catch(() => setNetwork([]));
  }, []);

  const createGroup = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await brokerApi.post("/groups", { name: newName, driver_ids: [] });
      toast.success("Group created.");
      setNewName("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create group.");
    } finally {
      setCreating(false);
    }
  };

  const toggleMember = async (group, driverId) => {
    const next = group.driver_ids.includes(driverId) ? group.driver_ids.filter((d) => d !== driverId) : [...group.driver_ids, driverId];
    try {
      await brokerApi.patch(`/groups/${group.id}`, { driver_ids: next });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update group.");
    }
  };

  const removeGroup = async (id) => {
    try {
      await brokerApi.del(`/groups/${id}`);
      toast.success("Group removed.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove group.");
    }
  };

  if (!groups) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-rr-orange" /></div>;

  return (
    <div data-testid="broker-groups-tab">
      <h2 className="font-heading text-xl font-bold text-rr-navy mb-5">Driver Groups</h2>
      <form onSubmit={createGroup} data-testid="broker-create-group-form" className="flex gap-2 max-w-md mb-6">
        <input
          required
          data-testid="create-group-name-input"
          placeholder="New group name (e.g. Southeast Reefer)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="flex-1 border border-rr-line rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-rr-orange"
        />
        <button type="submit" disabled={creating} data-testid="create-group-submit-button" className="bg-rr-navy text-white font-bold px-4 rounded-lg hover:bg-rr-orange transition-colors flex items-center gap-1.5 disabled:opacity-60">
          {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
        </button>
      </form>

      {groups.length === 0 && <p data-testid="broker-groups-empty-state" className="text-rr-muted text-sm">No groups yet — create one above.</p>}
      <div className="space-y-3">
        {groups.map((g) => (
          <GroupRow
            key={g.id}
            group={g}
            network={network}
            expanded={expandedId === g.id}
            onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
            onRemove={removeGroup}
            onToggleMember={toggleMember}
          />
        ))}
      </div>
    </div>
  );
};
